import {
  useBoolean,
  useToastr,
  usePagination,
  useTabStorage,
  usePaginationSessionStorage
} from '@laxmimanogna/code-quick-components';
import { createContext, useMemo, useState } from 'react';
import myAuditRepository from '../repositories/MyAuditRepository';
import {
  ARCHIVED,
  FILTER_OPTIONS,
  FILTER_OPTIONS_TO_CHART_STATUS_MAP,
  MY_AUDITS,
  NEW_UPLOAD,
  REBBUTTAL,
  SELF,
  URGENT,
} from '../constants/constants';
import auditSheetRepository from '../repositories/AuditSheetRepository';
import { DASHBOARD_PAGE_NAME, DASHBOARD_TABLE_NAME } from '../pages/dashboard/constants';

export const MyAuditContext = createContext({
  isAuditUploadListLoading: false,
  getMyAuditUpload: async () => { },
  getAuditNewUploads: async () => { },
  getAuditArchived: async () => { },
  getAuditRebuttals: async () => { },
  getAuditUrgent: async () => { },

  myAuditUploadList: [],
  auditArchivedList: [],
  auditNewUploadsList: [],
  auditRebuttalsList: [],
  auditUrgentList: [],

  myAuditUploadListCount: 0,
  auditNewUploadsListCount: 0,
  auditArchivedListCount: 0,
  auditRebuttalsListCount: 0,
  auditUrgentListCount: 0,

  filterParams: {},
  handleApplyFilter: () => { },
  getRecentAudits: async () => { },
  isRecentAuditLoading: false,
  recentAudits: [],
  monitorAuditHour: async () => { },

  // version 2
  getAllCharts: async () => { },
  chartStatusFilters: 'ALL',
  allCharts: [],
  isAllChartsLoading: false
});

const MyAuditProvider = props => {
  const toast = useToastr();
  const tabStorage = useTabStorage();

  const [isAuditUploadListLoading, iaState] = useBoolean(false);
  const [isRecentAuditLoading, iraState] = useBoolean(false);
  const [recentAudits, setRecentAudits] = useState([]);

  const [myAuditUploadList, setMyAuditUploadList] = useState([]);
  const [auditNewUploadsList, setAuditNewUploadsList] = useState([]);
  const [auditArchivedList, setAuditArchivedList] = useState([]);
  const [auditRebuttalsList, setAuditRebuttalsList] = useState([]);
  const [auditUrgentList, setAuditUrgentList] = useState([]);
  const [myAuditUploadListCount, setMyAuditUploadListCount] = useState(0);
  const [auditNewUploadsListCount, setAuditNewUploadsListCount] = useState(0);
  const [auditArchivedListCount, setAuditArchivedListCount] = useState(0);
  const [auditRebuttalsListCount, setAuditRebuttalsListCount] = useState(0);
  const [auditUrgentListCount, setAuditUrgentListCount] = useState(0);
  const [chartStatusFilters, setChartStatusFilters] = useState(FILTER_OPTIONS_TO_CHART_STATUS_MAP[FILTER_OPTIONS[tabStorage.getTab() || 0]]);

  const [isAllChartsLoading, iaclState] = useBoolean(false);
  const [allCharts, setAllCharts] = useState([])
  const getAllCharts1 = usePagination(getAllCharts)

  const {
    getPaginationSession
  } = usePaginationSessionStorage();

  const initialFilterParams = useMemo(() => ({
    newUploadStatus: '',
    // this will maintain the search text when we move to audit sheet and return. It will highlight the proper text
    searchText: getPaginationSession(DASHBOARD_PAGE_NAME, DASHBOARD_TABLE_NAME)?.search || '',
  }), []);
  const [filterParams, setFilterParams] = useState(
    Object.assign({}, initialFilterParams)
  );

  const getFilterParams = () => {
    const queryParams = {};
    const { searchText, ordering } = filterParams;
    if (searchText) queryParams['search'] = searchText;
    if (ordering) queryParams['ordering'] = ordering;

    return queryParams;
  };

  async function getAllCharts (paginationParams) {
    const filterParams = getFilterParams();
    // filterParams['user'] = SELF
    iaclState.on();
    try {
       let payload = { ...filterParams, ...paginationParams}
      if (chartStatusFilters && !('chart_statuses' in payload)) {
        payload['chart_statuses'] = chartStatusFilters
      }
      if ('chart_statuses' in payload && !payload.chart_statuses) {
        delete payload.chart_statuses;
      }
      const response = await myAuditRepository.getCharts(payload)
      const annotatedResponse = response.results.map(val => ({...val, batch_providers: [...new Set(val.subRows.map(row => (row.providers ?? []).map(chartProv => chartProv.name)).flat().filter(name => !!name))]}))
      setAllCharts(annotatedResponse)
      return {...response, results: annotatedResponse};
    } catch (err) {
        toast.showError({
            description: `Something went wrong`,
        });
    } finally {
        iaclState.off()
    }
  }

  async function getMyAuditUpload(paginationParams) {
    const filterParams = getFilterParams();
    if (NEW_UPLOAD) {
      filterParams.user = SELF;
      filterParams.status = MY_AUDITS;
    }
    try {
      iaState.on();
      const response = await myAuditRepository.getAuditUploads({
        ...paginationParams,
        ...filterParams,
      });
      setMyAuditUploadListCount(response.count);
      setMyAuditUploadList(response.results);
      return response
    } catch (e) {
      toast.showError({
        description: `${e}`,
      });
    } finally {
      iaState.off();
    }
  }

  async function getAuditNewUploads(paginationParams) {
    const filterParams = getFilterParams();
    if (NEW_UPLOAD) {
      //usershould not be sent
      filterParams.status = NEW_UPLOAD;
    }
    try {
      iaState.on();
      const response = await myAuditRepository.getAuditUploads({
        ...paginationParams,
        ...filterParams,
      });
      setAuditNewUploadsListCount(response.count);
      setAuditNewUploadsList(response.results);
      return response

    } catch (e) {
      toast.showError({
        description: `${e}`,
      });
    } finally {
      iaState.off();
    }
  }

  async function getAuditArchived(paginationParams) {
    const filterParams = getFilterParams();
    if (ARCHIVED) {
      filterParams.status = ARCHIVED;
      filterParams.user = SELF;
    }
    try {
      iaState.on();
      const response = await myAuditRepository.getAuditUploads({
        ...paginationParams,
        ...filterParams,
      });
      setAuditArchivedListCount(response.count);
      setAuditArchivedList(response.results);
      return response

    } catch (e) {
      toast.showError({
        description: `${e}`,
      });
    } finally {
      iaState.off();
    }
  }

  async function getAuditRebuttals(paginationParams) {
    const filterParams = getFilterParams();
    if (REBBUTTAL) {
      filterParams.status = REBBUTTAL;
    }
    try {
      iaState.on();
      const response = await myAuditRepository.getAuditUploads({
        ...paginationParams,
        ...filterParams,
      });
      setAuditRebuttalsListCount(response.count);
      setAuditRebuttalsList(response.results);
      return response

    } catch (e) {
      toast.showError({
        description: `${e}`,
      });
    } finally {
      iaState.off();
    }
  }

  async function getAuditUrgent(paginationParams) {
    const filterParams = getFilterParams();
    if (URGENT) {
      filterParams.urgent = true;
      filterParams.status = URGENT;
      filterParams.user = SELF;
    }
    try {
      iaState.on();
      const response = await myAuditRepository.getAuditUploads({
        ...paginationParams,
        ...filterParams,
      });
      setAuditUrgentListCount(response.count);
      setAuditUrgentList(response.results);
      return response

    } catch (e) {
      toast.showError({
        description: `${e}`,
      });
    } finally {
      iaState.off();
    }
  }
  async function getRecentAudits() {
    try {
      iraState.on();
      const response = await myAuditRepository.getRecentAudits();
      setRecentAudits(response);
    } catch (e) {
      toast.showError({
        description: `${e}`,
      });
    } finally {
      iraState.off();
    }
  }

  async function monitorAuditHour(uploadId) {
    try {
      await auditSheetRepository.auditHourMonitor(uploadId);
    } catch (err) {
      toast.showError({
        description: `${err}`,
      });
    }
  }

  const handleApplyFilter = selectedFilters => {
    setFilterParams(selectedFilters);
  };

  const getMyAuditUpload1 = usePagination(getMyAuditUpload);
  const getAuditNewUploads1 = usePagination(getAuditNewUploads);
  const getAuditArchived1 = usePagination(getAuditArchived);
  const getAuditRebuttals1 = usePagination(getAuditRebuttals);
  const getAuditUrgent1 = usePagination(getAuditUrgent);
  const mContext = {
    isAuditUploadListLoading,
    getMyAuditUpload: getMyAuditUpload1,
    getAuditNewUploads: getAuditNewUploads1,
    getAuditArchived: getAuditArchived1,
    getAuditRebuttals: getAuditRebuttals1,
    getAuditUrgent: getAuditUrgent1,

    myAuditUploadList,
    auditNewUploadsList,
    auditArchivedList,
    auditRebuttalsList,
    auditUrgentList,

    myAuditUploadListCount,
    auditNewUploadsListCount,
    auditArchivedListCount,
    auditRebuttalsListCount,
    auditUrgentListCount,

    filterParams,
    handleApplyFilter,
    getRecentAudits,
    isRecentAuditLoading,
    recentAudits,
    monitorAuditHour,

    // version 2
    getAllCharts: getAllCharts1,
    allCharts,
    chartStatusFilters,
    setChartStatusFilters,
    isAllChartsLoading

  };

  return (
    <MyAuditContext.Provider value={mContext}>
      {props.children}
    </MyAuditContext.Provider>
  );
};

export default MyAuditProvider;
